import type {SocialAccount} from '@Components/social-media/account.vo';
import {isFacebookAccount, isPinterestAccount, isTikTokAccount, MaxPostersPerPlatform} from '@Components/social-media/account.vo';
import type {MediaTypeLoadingStates, MediaTypePublishingParams, SocialMediaAccountId} from '@Components/social-media/post.vo';
import {
  GoogleBusinessProfilePostType,
  LinkedInPrivacySettings,
  PlatformMediaType,
  PostState,
  PrivacySettings,
  TikTokPublicAccountPrivacySettings,
  YouTubePrivacySettings,
} from '@Components/social-media/post.vo';
import type {
  HideSMRemoveAccountConfirmationResponse,
  PinterestBoard,
} from '@Components/social-media-wizard/social-media-wizard.types';
import {captionCharacterLimits} from '@Components/social-media-wizard/social-media-wizard.types';
import type {
  FacebookPublishingParams,
  GoogleBusinessProfilePublishingParams,
  GraphicMeta,
  GraphicsMetaMapType,
  InstagramPublishingParams,
  LinkedInPublishingParams,
  PinterestPublishingParams,
  SocialMediaPublishingParams,
  ThreadsPublishingParams,
  TikTokPublishingParams,
  TwitterPublishingParams,
  YouTubePublishingParams,
} from '@Components/social-media/publish-params.types';
import {PublishingMediaType} from '@Components/social-media/publish-params.types';
import type {AccountNeedsResizeMap, DesignSelection, PosterNeedsResizeInformation} from '@Components/social-media/social-media.types';
import {PosterResizeRequiredStatus, SocialPlatform, getReadableAccounts} from '@Components/social-media/social-media.types';
import {doesAccountNeedReconnection} from '@Components/social-media/account.vo';
import type {SocialAccountListItemTooltip} from '@Components/social-media-wizard/components/social-account-list-item';
import {
  isGoogleBusinessProfileAccountUnverified,
  isPostersCountValidForMultiPosting,
  isTwitterCurrentlyUnusable,
  isYouTubeCurrentlyUnusable,
} from '@Libraries/social-media-account.library';
import {getAllPageResizeInfoForAccount} from '@Libraries/social-media-resize.library';
import {isLoaded, isLoading, isLoadingOrNeedsLoading, LoadingStates} from '@Utils/loading.util';

const ALL_PLATFORM_MEDIA_TYPES = Object.values(PlatformMediaType);

/** Options bag for getAccountPublishabilityInfo. */
export interface AccountPublishabilityOpts {
  account: SocialAccount;
  posters: DesignSelection[];
  accountNeedsResizeMap: AccountNeedsResizeMap;
  designsGraphicsMetaMap: GraphicsMetaMapType;
  /** CSS class applied to info-icon tooltips (from the caller's SCSS module). */
  infoIconClass: string;
  /** True when any GraphicMeta still has an empty graphicURL (graphics metadata is still loading). */
  isGraphicsMetaLoading: boolean;
}

/** Single-pass merged result replacing isAccountPublishable + getTooltipIfNeeded. */
export interface AccountPublishabilityInfo {
  isPublishable: boolean;
  tooltip: SocialAccountListItemTooltip | undefined;
}

export const getAllGraphicMetas = (map: GraphicsMetaMapType): GraphicMeta[] =>
  Object.values(map).flatMap((pageMeta) => Object.values(pageMeta));

export const getUnsupportedDesignsForAccount = (accountId: SocialMediaAccountId, accountNeedsResizeMap: AccountNeedsResizeMap): DesignSelection[] => {
  if (!accountNeedsResizeMap[accountId]) {
    return [];
  }

  const unsupported: DesignSelection[] = [];
  const resizeInfoForDesigns = accountNeedsResizeMap[accountId];

  for (const posterHashedId of Object.keys(resizeInfoForDesigns)) {
    for (const pageHashedId of Object.keys(resizeInfoForDesigns[posterHashedId])) {
      if (resizeInfoForDesigns[posterHashedId][pageHashedId].needsResizing === PosterResizeRequiredStatus.NOT_SUPPORTED) {
        unsupported.push({posterHashedId, pageHashedId});
      }
    }
  }

  return unsupported;
};

export const getFirstUnsupportedResizeInfo = (
  accountId: SocialMediaAccountId,
  accountNeedsResizeMap: AccountNeedsResizeMap,
  unsupportedDesigns: DesignSelection[]
): PosterNeedsResizeInformation | null => {
  if (unsupportedDesigns.length === 0) {
    return null;
  }
  const {posterHashedId, pageHashedId} = unsupportedDesigns[0];
  return accountNeedsResizeMap[accountId][posterHashedId][pageHashedId];
};

export const hasAnyPosterResizeStatus = (accountId: SocialMediaAccountId, accountNeedsResizeMap: AccountNeedsResizeMap, status: PosterResizeRequiredStatus): boolean => {
  if (!accountNeedsResizeMap[accountId]) {
    return false;
  }
  const allPageResizeInfo = getAllPageResizeInfoForAccount(accountNeedsResizeMap, accountId);
  return allPageResizeInfo.some((info) => info.needsResizing === status);
};

export const isMultiPostingMediaValid = (designsGraphicsMetaMap: GraphicsMetaMapType): boolean => {
  const graphicMetas = getAllGraphicMetas(designsGraphicsMetaMap);

  if (graphicMetas.length === 1) {
    return true;
  }

  for (const graphicMeta of graphicMetas) {
    if (graphicMeta.isVideo) {
      return false;
    }
  }

  return true;
};

const getDisabledTextForMediaType = (mediaType: PublishingMediaType, resizeInfo: PosterNeedsResizeInformation | null): string => {
  if (!resizeInfo) {
    return '';
  }

  if (resizeInfo.notSupportedCustomMessage) {
    return resizeInfo.notSupportedCustomMessage;
  }

  switch (mediaType) {
    case PublishingMediaType.IMAGE:
      return window.i18next.t('pmwjs_images_cannot_be_published');
    case PublishingMediaType.VIDEO:
      return window.i18next.t('pmwjs_videos_cannot_be_published');
    default:
      return '';
  }
};

const getMultiPostingPlatformLimitWarning = (account: SocialAccount): string => {
  const readableAccountPlatform = getReadableAccounts(account.type);
  const platformLimit = `${MaxPostersPerPlatform[account.type] ?? ''}`;

  return platformLimit === '1'
    ? window.i18next.t('pmwjs_posters_exceeded_multi_posting_info', {account: readableAccountPlatform})
    : window.i18next.t('pmwjs_posters_exceeded_multi_posting_info_plural', {
        account: readableAccountPlatform,
        limit: platformLimit,
      });
};

/**
 * Single-pass replacement for the parallel `isAccountPublishable` + `getTooltipIfNeeded`
 * functions that existed in SocialAccountsList. Precedence order matches `getTooltipIfNeeded`
 * exactly; `isPublishable` is false for all veto conditions that `isAccountPublishable` vetoed.
 *
 * Conditions that set isPublishable=false but produce no tooltip (plan lock, reconnect-needed)
 * are handled first; the component renders dedicated UI (crown / reconnect icon) for those.
 */
export const getAccountPublishabilityInfo = (opts: AccountPublishabilityOpts): AccountPublishabilityInfo => {
  const {account, posters, accountNeedsResizeMap, designsGraphicsMetaMap, infoIconClass, isGraphicsMetaLoading} = opts;

  // Plan lock: unpublishable, no tooltip (list item renders crown icon separately).
  if (account.isLockedByPlanLimit) {
    return {isPublishable: false, tooltip: undefined};
  }

  // Reconnect required: unpublishable, no tooltip (list item renders reconnect icon separately).
  if (doesAccountNeedReconnection(account)) {
    return {isPublishable: false, tooltip: undefined};
  }

  // Facebook personal profile — checked before FACEBOOK_GROUP to match getTooltipIfNeeded precedence.
  if (account.type === SocialPlatform.FACEBOOK_PROFILE) {
    return {
      isPublishable: false,
      tooltip: {icon: 'icon-exclamation-triangle', text: window.i18next.t('pmwjs_facebook_profile_unpostable')},
    };
  }

  if (account.type === SocialPlatform.FACEBOOK_GROUP) {
    return {
      isPublishable: false,
      tooltip: {icon: 'icon-exclamation-triangle', text: window.i18next.t('pmwjs_facebook_group_unpostable')},
    };
  }

  if (isTwitterCurrentlyUnusable(account)) {
    return {
      isPublishable: false,
      tooltip: {icon: 'icon-exclamation-triangle', text: window.i18next.t('pmwjs_twitter_suspension_message')},
    };
  }

  if (isYouTubeCurrentlyUnusable(account)) {
    return {
      isPublishable: false,
      tooltip: {icon: 'icon-exclamation-triangle', text: window.i18next.t('pmwjs_youtube_outage_message')},
    };
  }

  if (isGoogleBusinessProfileAccountUnverified(account)) {
    return {
      isPublishable: false,
      tooltip: {icon: 'icon-exclamation-triangle', text: window.i18next.t('pmwjs_google_business_profile_unverified_message')},
    };
  }

  if (!isPostersCountValidForMultiPosting(posters, account)) {
    return {
      isPublishable: false,
      tooltip: {icon: 'icon-info', text: getMultiPostingPlatformLimitWarning(account), classes: infoIconClass},
    };
  }

  // Pinterest checked before Facebook to match getTooltipIfNeeded precedence.
  if (isPinterestAccount(account) && !isMultiPostingMediaValid(designsGraphicsMetaMap)) {
    return {
      isPublishable: false,
      tooltip: {icon: 'icon-info', text: window.i18next.t('pmwjs_pinterest_multi_posting_unsupported_videos'), classes: infoIconClass},
    };
  }

  if (isFacebookAccount(account) && !isMultiPostingMediaValid(designsGraphicsMetaMap)) {
    return {
      isPublishable: false,
      tooltip: {icon: 'icon-info', text: window.i18next.t('pmwjs_facebook_multi_posting_unsupported_videos'), classes: infoIconClass},
    };
  }

  if (isTikTokAccount(account) && !isMultiPostingMediaValid(designsGraphicsMetaMap)) {
    return {
      isPublishable: false,
      tooltip: {icon: 'icon-info', text: window.i18next.t('pmwjs_tiktok_multi_posting_unsupported_videos'), classes: infoIconClass},
    };
  }

  const unsupportedDesigns = getUnsupportedDesignsForAccount(account.id, accountNeedsResizeMap);

  if (unsupportedDesigns.length > 0) {
    if (isGraphicsMetaLoading) {
      return {isPublishable: false, tooltip: undefined};
    }
    const {posterHashedId, pageHashedId} = unsupportedDesigns[0];
    const mediaType = designsGraphicsMetaMap[posterHashedId][pageHashedId].isVideo ? PublishingMediaType.VIDEO : PublishingMediaType.IMAGE;
    const firstUnsupportedResizeInfo = getFirstUnsupportedResizeInfo(account.id, accountNeedsResizeMap, unsupportedDesigns);
    return {
      isPublishable: false,
      tooltip: {icon: 'icon-info', text: getDisabledTextForMediaType(mediaType, firstUnsupportedResizeInfo)},
    };
  }

  // Resize/optimisation warnings: publishable, but show informational tooltip.
  if (hasAnyPosterResizeStatus(account.id, accountNeedsResizeMap, PosterResizeRequiredStatus.RESIZE_NEEDED)) {
    return {
      isPublishable: true,
      tooltip: {icon: 'icon-resize', text: window.i18next.t('pmwjs_your_design_will_be_resized')},
    };
  }

  if (hasAnyPosterResizeStatus(account.id, accountNeedsResizeMap, PosterResizeRequiredStatus.BACKEND_OPTIMIZATION_NEEDED)) {
    return {
      isPublishable: true,
      tooltip: {icon: 'icon-resize', text: window.i18next.t('pmwjs_your_design_will_be_processed')},
    };
  }

  return {isPublishable: true, tooltip: undefined};
};

const HIDE_SM_REMOVE_ACCOUNT_CONFIRMATION_CACHE_KEY_PREFIX = 'hide_sm_remove_account_confirmation_';

export const getAccountForId = (accounts: SocialAccount[], socialAccountId: SocialMediaAccountId): SocialAccount | null => {
  let matchedAccount = null;
  accounts.forEach((account) => {
    if (socialAccountId === account.id) {
      matchedAccount = account;
    }
  });

  return matchedAccount;
};

export const getEmptyPublishingParamsForAccount = (account: SocialAccount): SocialMediaPublishingParams | null => {
  let publishingParams: SocialMediaPublishingParams | null;

  switch (account.type) {
    case SocialPlatform.FACEBOOK:
    case SocialPlatform.FACEBOOK_PAGE:
    case SocialPlatform.FACEBOOK_PROFILE:
    case SocialPlatform.FACEBOOK_GROUP:
      publishingParams = {
        caption: '',
        idSocialMediaAccount: account.id,
        title: '',
        publishedURL: null,
        publishErrorMessage: null,
        state: PostState.DRAFT,
        isOriginalDeleted: false,
      } as FacebookPublishingParams;
      break;
    case SocialPlatform.INSTAGRAM:
      publishingParams = {
        caption: '',
        idSocialMediaAccount: account.id,
        publishedURL: null,
        publishErrorMessage: null,
        state: PostState.DRAFT,
        isOriginalDeleted: false,
      } as InstagramPublishingParams;
      break;
    case SocialPlatform.YOUTUBE:
      publishingParams = {
        caption: '',
        idSocialMediaAccount: account.id,
        privacySettings: YouTubePrivacySettings.PUBLIC,
        title: '',
        publishedURL: null,
        publishErrorMessage: null,
        state: PostState.DRAFT,
        isOriginalDeleted: false,
      } as YouTubePublishingParams;
      break;
    case SocialPlatform.TWITTER:
      publishingParams = {
        caption: '',
        idSocialMediaAccount: account.id,
        publishedURL: null,
        publishErrorMessage: null,
        state: PostState.DRAFT,
        isOriginalDeleted: false,
      } as TwitterPublishingParams;
      break;
    case SocialPlatform.LINKEDIN:
    case SocialPlatform.LINKEDIN_PAGE:
    case SocialPlatform.LINKEDIN_PROFILE:
      publishingParams = {
        caption: '',
        idSocialMediaAccount: account.id,
        privacySettings: LinkedInPrivacySettings.PUBLIC,
        title: '',
        publishedURL: null,
        publishErrorMessage: null,
        state: PostState.DRAFT,
        isOriginalDeleted: false,
      } as LinkedInPublishingParams;
      break;
    case SocialPlatform.TIKTOK:
      publishingParams = {
        privacySettings: TikTokPublicAccountPrivacySettings.PUBLIC_TO_EVERYONE,
        disableComment: false,
        disableDuet: false,
        disableStitch: false,
        isPromotingThirdParty: false,
        isSelfBrand: false,
        caption: '',
        title: '',
        idSocialMediaAccount: account.id,
        publishedURL: null,
        publishErrorMessage: null,
        state: PostState.DRAFT,
        isOriginalDeleted: false,
      } as TikTokPublishingParams;
      break;
    case SocialPlatform.PINTEREST:
      publishingParams = {
        title: '',
        caption: '',
        websiteLink: '',
        idSocialMediaAccount: account.id,
        publishedURL: null,
        state: PostState.DRAFT,
        publishId: null,
        publishErrorMessage: null,
        boardId: null,
        privacySettings: PrivacySettings.PUBLIC,
        graphicsMetaMap: {},
        isOriginalDeleted: false,
      } as PinterestPublishingParams;
      break;
    case SocialPlatform.GOOGLE_BUSINESS_PROFILE:
      publishingParams = {
        caption: '',
        postType: GoogleBusinessProfilePostType.STANDARD,
        ctaType: null,
        ctaURL: '',
        eventStartTime: null,
        eventEndTime: null,
        idSocialMediaAccount: account.id,
        publishedURL: null,
        publishErrorMessage: null,
        publishId: null,
        state: PostState.DRAFT,
        graphicsMetaMap: {},
        isOriginalDeleted: false,
      } as GoogleBusinessProfilePublishingParams;
      break;
    case SocialPlatform.THREADS:
      publishingParams = {
        caption: '',
        idSocialMediaAccount: account.id,
        publishedURL: null,
        publishErrorMessage: null,
        state: PostState.DRAFT,
        graphicsMetaMap: {},
        isOriginalDeleted: false,
      } as ThreadsPublishingParams;
      break;
    default:
      publishingParams = null;
  }

  return publishingParams;
};

export const isMediaTypeSupportedForAccount = (accountPlatform: SocialPlatform, mediaType: PublishingMediaType): boolean => {
  switch (accountPlatform) {
    case SocialPlatform.FACEBOOK:
    case SocialPlatform.FACEBOOK_PAGE:
    case SocialPlatform.FACEBOOK_PROFILE:
    case SocialPlatform.FACEBOOK_GROUP:
    case SocialPlatform.INSTAGRAM:
    case SocialPlatform.TWITTER:
    case SocialPlatform.LINKEDIN:
    case SocialPlatform.LINKEDIN_PAGE:
    case SocialPlatform.LINKEDIN_PROFILE:
    case SocialPlatform.THREADS:
      return true;

    case SocialPlatform.YOUTUBE:
    case SocialPlatform.TIKTOK:
      return mediaType === PublishingMediaType.VIDEO;

    case SocialPlatform.GOOGLE_BUSINESS_PROFILE:
      return mediaType === PublishingMediaType.IMAGE;

    default:
      return false;
  }
};

export const getCharacterLimitForAccountPost = (account: SocialAccount): number => {
  switch (account.type) {
    case SocialPlatform.TWITTER:
      return account.isVerifiedTwitterUser ? 10000 : captionCharacterLimits[SocialPlatform.TWITTER];
    default:
      return captionCharacterLimits[account.type];
  }
};

export const haveAllPublishingStatesLoaded = (
  accountIdsToPublishTo: number[],
  publishingStatusForAllAccounts: Record<SocialMediaAccountId, MediaTypeLoadingStates>,
  publishingParams: Record<SocialMediaAccountId, MediaTypePublishingParams>
): boolean => {
  for (const accountId of accountIdsToPublishTo) {
    const reelParams = publishingParams[accountId]?.REELS;
    const reelLoadingState = publishingStatusForAllAccounts[accountId]?.REELS;

    const postParams = publishingParams[accountId]?.POST;
    const postLoadingState = publishingStatusForAllAccounts[accountId]?.POST;

    const storyParams = publishingParams[accountId]?.STORIES;
    const storyLoadingState = publishingStatusForAllAccounts[accountId]?.STORIES;

    if (reelParams && (reelLoadingState === undefined || isLoadingOrNeedsLoading(reelLoadingState))) {
      return false;
    }

    if (postParams && (postLoadingState === undefined || isLoadingOrNeedsLoading(postLoadingState))) {
      return false;
    }

    if (storyParams && (storyLoadingState === undefined || isLoadingOrNeedsLoading(storyLoadingState))) {
      return false;
    }

    if (!reelLoadingState || !postLoadingState || !storyLoadingState) {
      return false;
    }
  }

  return true;
};

/**
 * Whether every publish being awaited has finished — success OR failure (a failed / NOT_LOADED slot counts
 * as settled, unlike {@link haveAllPublishingStatesLoaded}). When `publishingParams` is supplied, only the
 * media-type slots that actually have params (i.e. are being published) need to be settled; without it,
 * every media type must have a settled status.
 */
export const haveAllPublishingStatesSettled = (
  accountIdsToPublishTo: number[],
  publishingStatusForAllAccounts: Record<SocialMediaAccountId, MediaTypeLoadingStates>,
  publishingParams?: Record<SocialMediaAccountId, MediaTypePublishingParams>
): boolean => {
  for (const accountId of accountIdsToPublishTo) {
    for (const mediaType of ALL_PLATFORM_MEDIA_TYPES) {
      if (publishingParams && !publishingParams[accountId]?.[mediaType]) {
        continue;
      }

      const loadingState = publishingStatusForAllAccounts[accountId]?.[mediaType];
      if (loadingState === undefined || isLoading(loadingState)) {
        return false;
      }
    }
  }

  return true;
};

export interface PublishedSlotCounts {
  publishedCount: number;
  totalCount: number;
}

/**
 * Counts the slots actually being published (those with publishing params) and how many of them reached
 * LOADED. The params check matters: a media type without params is initialised to LOADED as "nothing to
 * publish", and counting those would report successes for a publish where every real slot failed.
 */
export const getPublishedSlotCounts = (
  accountIdsToPublishTo: number[],
  publishingStatusForAllAccounts: Record<SocialMediaAccountId, MediaTypeLoadingStates>,
  publishingParams: Record<SocialMediaAccountId, MediaTypePublishingParams>
): PublishedSlotCounts => {
  let publishedCount = 0;
  let totalCount = 0;

  for (const accountId of accountIdsToPublishTo) {
    for (const mediaType of ALL_PLATFORM_MEDIA_TYPES) {
      if (!publishingParams[accountId]?.[mediaType]) {
        continue;
      }

      totalCount += 1;
      const loadingState = publishingStatusForAllAccounts[accountId]?.[mediaType];

      if (loadingState !== undefined && isLoaded(loadingState)) {
        publishedCount += 1;
      }
    }
  }

  return {publishedCount, totalCount};
};

export const hasAnyPublishSucceeded = (
  accountIdsToPublishTo: number[],
  publishingStatusForAllAccounts: Record<SocialMediaAccountId, MediaTypeLoadingStates>,
  publishingParams: Record<SocialMediaAccountId, MediaTypePublishingParams>
): boolean => {
  for (const accountId of accountIdsToPublishTo) {
    for (const mediaType of Object.values(PlatformMediaType)) {
      if (publishingParams[accountId]?.[mediaType] && publishingStatusForAllAccounts[accountId]?.[mediaType] === LoadingStates.LOADED) {
        return true;
      }
    }
  }

  return false;
};

export const getHideSMRemoveAccountConfirmationCacheKey = (): string => {
  return HIDE_SM_REMOVE_ACCOUNT_CONFIRMATION_CACHE_KEY_PREFIX + (window.PMW.getUserId() as string);
};

export const setHideSMRemoveAccountConfirmationCache = (value: number): void => {
  window.PMW.setCacheItem(getHideSMRemoveAccountConfirmationCacheKey(), value);
};

export const getHideSMRemoveAccountConfirmationCache = (): boolean | null => {
  const cachedValue = window.PMW.getCacheItem(getHideSMRemoveAccountConfirmationCacheKey()) as string | null;

  if (cachedValue === null) {
    return null;
  }

  return parseInt(cachedValue, 10) === 1;
};

export const getHideSMRemoveAccountConfirmationPreference = async (): Promise<boolean> => {
  const cachedValue = getHideSMRemoveAccountConfirmationCache();

  if (cachedValue !== null) {
    return cachedValue;
  }

  if (!window.PMW.isUserLoggedIn()) {
    return false;
  }

  try {
    const data = (await window.PMW.readLocal('socialmedia/getRemoveAccountConfirmationPreferenceForUser')) as HideSMRemoveAccountConfirmationResponse;
    setHideSMRemoveAccountConfirmationCache(data.hideConfirmation ? 1 : 0);
    return data.hideConfirmation;
  } catch (error) {
    console.error(error);
    return false;
  }
};

export const doHideRemoveAccountConfirmationForUser = async (): Promise<void> => {
  try {
    await window.PMW.writeLocal('socialmedia/doHideRemoveAccountConfirmationForUser');
    setHideSMRemoveAccountConfirmationCache(1);
  } catch (error) {
    console.error(error);
  }
};

export const getPinterestBoards = async (pinterestAccountId: number): Promise<PinterestBoard[]> => {
  return (await window.PMW.readLocal('/socialmedia/getPinterestBoardsForUser', {pinterestAccountId})) as PinterestBoard[];
};

export const createPinterestBoard = async (pinterestAccountId: number, boardName: string): Promise<PinterestBoard> => {
  return (await window.PMW.writeLocal('/socialmedia/createPinterestBoardsForUser', {
    pinterestAccountId,
    boardName,
  })) as PinterestBoard;
};
